import { useCallback, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { EyeIcon } from '@heroicons/react/24/solid'
import { EyeSlashIcon } from '@heroicons/react/24/solid'
import { Button } from '@/components/ui/button'
import { useCADPageStore } from '@/state/cad'
import { cn } from '@/utils'

import type { GLTFObject } from '@/lib/cad/GLTFObject'
import type { ASMTreeNode } from '@/state'
import { useOperationSteps } from '@/services/queries/operation_steps'
import { useCADQuery } from '@/services/queries/cads'

export const HideAllPartsButton = ({
  nodes,
  gltf,
}: {
  nodes: ASMTreeNode[]
  gltf: GLTFObject
}) => {
  const { isLoading: isLoadingCadData, data: cadData } = useCADQuery()
  const { isLoading: isLoadingSteps, steps } = useOperationSteps()

  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))
  const allPartsHidden = useCADPageStore((state) => state.allPartsHidden)
  const operationStep = useCADPageStore(
    useShallow((state) => state.operationStep),
  )
  const explosions = useCADPageStore((state) => state.explosions)
  const isExplosionLinesEnabled = useCADPageStore(
    (state) => state.isExplosionLinesEnabled,
  )
  const showFullModel = useCADPageStore((state) => state.showFullModel)
  const hidePreviousOperations = useCADPageStore(
    (state) => state.hidePreviousOperations,
  )
  const rawAssemblyTree = useMemo(
    () => cadData.rawAssemblyTree,
    [cadData.rawAssemblyTree],
  )

  const toggleAllParts = useCallback(() => {
    const hiddenParts = !allPartsHidden ? nodes.map((node) => node.uuid) : []

    if (
      allPartsHidden &&
      operationStep &&
      (operationStep.isActive || operationStep.selectFromCad) &&
      rawAssemblyTree
    ) {
      const activeStep = steps.find((s) => s.id === operationStep.stepId)

      if (activeStep) {
        gltf.setVisibilityForOperationSteps({
          assemblyTree: rawAssemblyTree,
          steps,
          hiddenParts,
          activeStep,
          explosions,
          isExplosionLinesEnabled,
          showFullModel,
          hidePreviousOperations,
        })

        activeStep.assembly_group_ids.forEach((assemblyGroupId) => {
          const activeNode = nodes.find((n) => n.uuid === assemblyGroupId)
          if (activeNode) {
            gltf.setVisibility(activeNode.instance, allPartsHidden)
          }
        })
      }
    } else {
      nodes.map((node) => {
        gltf.setVisibility(node.instance, allPartsHidden)
      })
    }

    // Always update hidden parts
    setCadPageState({
      hiddenParts,
      allPartsHidden: !allPartsHidden,
    })
  }, [
    nodes,
    allPartsHidden,
    operationStep,
    setCadPageState,
    gltf,
    steps,
    rawAssemblyTree,
    explosions,
    isExplosionLinesEnabled,
  ])

  const Icon = allPartsHidden ? EyeIcon : EyeSlashIcon

  if (isLoadingSteps || isLoadingCadData) return null

  return (
    <div className="px-4 ">
      <Button
        className={cn(
          'flex items-center space-x-2 rounded-full text-black shadow-none border',
          {
            'bg-primary-10 border-primary-20': allPartsHidden,
            'bg-gray-50 border-gray-300': !allPartsHidden,
          },
        )}
        onClick={toggleAllParts}
        size="sm"
      >
        <span className="text-sm font-normal">
          {allPartsHidden ? 'Show All' : 'Hide All'}
        </span>
        <Icon
          className={cn('w-4 h-4', {
            'text-primary-50': allPartsHidden,
            'text-gray-400': !allPartsHidden,
          })}
        />
      </Button>
    </div>
  )
}
